/**
 * Think Education survey (LE-416).
 *
 * Think Education users see their Google Form after sign-in. A Google Apps
 * Script on the form's response sheet answers "has this email responded?",
 * so LiFT needs no backend changes. See docs/think-education-survey.md.
 */

const CLOSED_KEY_PREFIX = 'lift-think-education-survey-closed:';

export interface ThinkEducationSurveyConfig {
  /** Think Education has more than one LiFT tenant (e.g. Think Transcend). */
  tenantSlugs: string[];
  formUrl: string;
  emailEntryId: string;
  checkUrl: string;
}

function readConfig(): ThinkEducationSurveyConfig | null {
  const tenantSlugs = (import.meta.env.VITE_THINK_EDU_SURVEY_TENANT_SLUGS ?? '')
    .split(',')
    .map((slug) => slug.trim())
    .filter(Boolean);
  const formUrl = import.meta.env.VITE_THINK_EDU_SURVEY_FORM_URL?.trim();
  const rawEntryId = import.meta.env.VITE_THINK_EDU_SURVEY_EMAIL_ENTRY_ID?.trim();
  const checkUrl = import.meta.env.VITE_THINK_EDU_SURVEY_CHECK_URL?.trim();

  if (!tenantSlugs.length || !formUrl || !rawEntryId || !checkUrl) {
    return null;
  }

  const emailEntryId = rawEntryId.startsWith('entry.')
    ? rawEntryId
    : `entry.${rawEntryId}`;

  return { tenantSlugs, formUrl, emailEntryId, checkUrl };
}

/** Null when the survey isn't configured for this deployment. */
export const thinkEducationSurveyConfig = readConfig();

/** Embeddable Google Form URL with the user's LiFT email prefilled. */
export function buildThinkEducationSurveyFormUrl(
  config: ThinkEducationSurveyConfig,
  email: string
): string {
  const url = new URL(config.formUrl);
  url.searchParams.set('usp', 'pp_url');
  url.searchParams.set(config.emailEntryId, email);
  url.searchParams.set('embedded', 'true');
  return url.toString();
}

/**
 * Ask the Apps Script whether this email has responded.
 * Plain fetch with no custom headers: Apps Script only answers simple CORS
 * requests, and our API token must not be sent to Google.
 */
export async function fetchHasRespondedToThinkEducationSurvey(
  config: ThinkEducationSurveyConfig,
  email: string,
  signal?: AbortSignal
): Promise<boolean> {
  const url = new URL(config.checkUrl);
  url.searchParams.set('email', email.trim().toLowerCase());

  const response = await fetch(url.toString(), {
    signal,
    credentials: 'omit',
  });
  if (!response.ok) {
    throw new Error(`Survey check failed with status ${response.status}`);
  }

  const body = (await response.json()) as { responded?: unknown };
  return body?.responded === true;
}

/** "Not now" / "Continue" close the modal for the rest of this session. */
export function isThinkEducationSurveyClosed(userId: string | number): boolean {
  try {
    return sessionStorage.getItem(`${CLOSED_KEY_PREFIX}${userId}`) === '1';
  } catch {
    return false;
  }
}

export function markThinkEducationSurveyClosed(userId: string | number): void {
  try {
    sessionStorage.setItem(`${CLOSED_KEY_PREFIX}${userId}`, '1');
  } catch {
    // Storage unavailable: the modal may reappear on reload, which is harmless.
  }
}

/** Called on sign-out so the modal returns at the next sign-in. */
export function clearThinkEducationSurveyClosed(): void {
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(CLOSED_KEY_PREFIX))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Storage unavailable: nothing to clear.
  }
}
